/* eslint-disable react/function-component-definition */
import React, { ComponentType, useState } from 'react'
import Filter from 'shared-svg/Filter.svg'
import styleUtils from 'shared-components/styles/util.module.css'
import {
  DSBestListComparisonTableState,
  DSBestListRatingsPage,
  DSBestListRatingsTableColumn,
  DSBestListRatingsTableRow,
  DSProductComparisonChartData,
  DSResource,
  DSSeeMoreProductPopup,
  DSSubmitProductVoteResult,
  DSVote,
  NCComponentInterface,
} from 'shared-definitions/types'
import { useArticleViewed } from '../hooks/use-article-viewed'
import ScrollEventsListener from '../service/ScrollEventsListener'
import Disclaimer from '../ui/Disclaimer'
import BreadCrumbs from '../ui/BreadCrumbs'
import PostShortLine from '../ui/PostShortLine'
import PostTitle from '../ui/PostTitle'
import SplitterLine from '../ui/SplitterLine'
import styles from './BestListRatingsTemplate.module.css'
import AboutExpert from '../ui/AboutExpert'
import { BestListComparisonTable } from '../ui/BestListComparisonTable'
import ContentLayout from '../ui/ContentLayout'
import { ProductComparisonChart } from '../ui/ProductComparisonChart'
import clsx from 'clsx'
import { CompareDealGallery } from '../ui/CompareDealGallery'
import { logError } from 'shared-code/log'
import dynamic from 'next/dynamic'
import { NotificationsContext } from '../contexts/NotificationsContext'
import { CommonDataContext } from '../contexts/CommonDataContext'
import { ScrollUpContext } from '../contexts/ScrollUpContext'
import ArticleConversation from '../comments/conversation/ArticleConversation'
import LocalPopup from '../layout/LocalPopup'
import { useGlobalKey } from '../hooks/use-global-key'
import RWButton from '../buttons/RWButton'
import { BestListComparisonFilter } from '../ui/BestListComparisonFilter'
import { ResponsiveContext } from '../contexts/ResponsiveContext'
import { useOutsideClick } from '../hooks/use-outside-click'
import { isEqualBestListFilter } from 'shared-code/compare-best-list-filter'
import { useAbortableEffect } from '../hooks/use-abortable-effect'
import PostTags from '../ui/PostTags'
import { useDocumentScrollDisabled } from '../hooks/use-document-scroll-disabled'

const SeeMoreProductPopup = dynamic(() => import('../ui/SeeMoreProductPopup'), { ssr: false })

interface TypesenseResultsState {
  table: {
    columns: DSBestListRatingsTableColumn[]
    rows: DSBestListRatingsTableRow[]
  }
  chart: DSProductComparisonChartData
  primaryScoresInfo: DSBestListRatingsPage<any>['productsPrimaryScoresInfo']
}

export interface BestListRatingsTemplateProps<T extends DSResource> {
  nc: ComponentType<NCComponentInterface<T>>
  page: DSBestListRatingsPage<T>
  onSeeMore: (
    productId: string,
    signal?: AbortSignal
  ) => Promise<{
    seeMore: Omit<DSSeeMoreProductPopup, 'compareProducts'>
  }>
  onVote: (productId: string, vote: DSVote) => Promise<DSSubmitProductVoteResult>
  onGetResults(
    filter: DSBestListComparisonTableState,
    signal?: AbortSignal
  ): Promise<TypesenseResultsState>
}

function BestListRatingsTemplate<T extends DSResource>({
  page,
  onSeeMore,
  onVote,
  onGetResults,
  nc: NC,
}: BestListRatingsTemplateProps<T>): React.ReactElement {
  const defaultPageState = {
    table: page.comparisonTable.defaultContent,
    chart: page.productChart.chart.chart,
    primaryScoresInfo: page.productsPrimaryScoresInfo,
  } as const

  const [pageState, setPageState] = useState<TypesenseResultsState>(defaultPageState)
  const [pending, setPending] = useState(false)
  const { enqueue } = NotificationsContext.useContainer()
  const { scrolledUp } = ScrollUpContext.useContainer()
  const [seeMorePopupContent, setSeeMorePopupContent] = useState<DSSeeMoreProductPopup | null>(null)
  const [seeMorePopupOpened, setSeeMorePopupOpened] = useState(false)

  useDocumentScrollDisabled(seeMorePopupOpened)

  const {
    common: { strings },
  } = CommonDataContext.useContainer()

  useGlobalKey({ code: 'Escape' }, () => setSeeMorePopupOpened(false))

  // filter handling
  const [currentFilterState, setCurrentFilterState] = useState(
    page.filter.filterSettings.form.defaultState
  )
  const { ready, md } = ResponsiveContext.useContainer()
  const [filterOpen, setFilterOpen] = useState(false)
  const ref = useOutsideClick<HTMLDivElement>(() => {
    setFilterOpen(false)
  }, [])

  useGlobalKey({ code: 'Escape' }, () => {
    setFilterOpen(false)
  })

  useAbortableEffect(
    // eslint-disable-next-line @typescript-eslint/require-await, @typescript-eslint/no-unused-vars
    async signal => {
      if (isEqualBestListFilter(currentFilterState, page.filter.filterSettings.form.defaultState)) {
        setPageState(defaultPageState)
        return
      }

      setPending(true)

      try {
        const results = await onGetResults(currentFilterState, signal)
        setPageState(results)
      } catch (e) {
        enqueue({
          variant: 'error',
          msg: strings.msgError,
          duration: 5000,
        })
        logError('BestListRatingsTemplate onGetResults', e)
      } finally {
        setPending(false)
      }
    },
    [currentFilterState]
  )

  const handleVote = async (
    productId: string,
    vote: DSVote
  ): Promise<DSSubmitProductVoteResult | null> => {
    try {
      const voteResult = await onVote(productId, vote)
      enqueue({
        variant: 'success',
        msg: strings.msgSuccess,
        duration: 3000,
      })
      return voteResult
    } catch (e) {
      enqueue({
        variant: 'error',
        msg: strings.msgError,
        duration: 5000,
      })
      logError('BestListRatingsTemplate handleVote', e)
    }

    return null
  }

  const handleSeeMore = (productId: string, signal?: AbortSignal): void => {
    setPending(true)
    onSeeMore(productId, signal)
      .then(({ seeMore }) => {
        const currentProduct = pageState.primaryScoresInfo.allProducts.find(p => p.id === productId)
        if (!currentProduct) {
          throw new Error(`Product with ID ${productId} not found in primary scores`)
        }

        setSeeMorePopupContent({
          ...seeMore,
          compareProducts: {
            title: page.filterAdditionalInfo.strings.compareProductScores,
            currentProduct,
            altProducts: pageState.primaryScoresInfo.allProducts.filter(p => p.id !== productId),
            comparedFeatures: pageState.primaryScoresInfo.comparedFeatures,
          },
        })
        setSeeMorePopupOpened(true)
      })
      .catch(e => {
        enqueue({
          variant: 'error',
          msg: strings.msgError,
          duration: 5000,
        })
        logError('BestListRatingsTemplate handleSeeMore', e)
      })
      .finally(() => {
        setPending(false)
      })
  }

  return (
    <>
      <ScrollEventsListener />
      <Disclaimer />
      <div
        className={clsx(styleUtils.wrapper, styles.container, {
          [styles.pending]: pending,
        })}
        ref={useArticleViewed({
          articleId: page.meta?.postId?.toString() ?? null,
          title: page.title,
          url: page.pagePath,
          categoryLevel: 1,
          categoryTitle: page.meta.categories[0]?.label ?? '',
          author: page.authors.authoredBy.map(author => author.name).join(', '),
          wordCount: parseInt(page.meta.dataLayer?.word_count?.toString() ?? '0', 10),
          date: page.meta.dataLayer?.original_pub_date?.toString() ?? '',
        })}
      >
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <BreadCrumbs list={page.breadcrumbs} className={styles.breadCrumbs} />
            <PostTitle
              title={page.title}
              subtitle={
                page.slots.top.length > 0 ? (
                  <>
                    {page.slots.top.map((item, index) => (
                      <NC key={index} index={index} data={item} tpl="event" />
                    ))}
                  </>
                ) : null
              }
            />
            <PostShortLine
              post={{ ...page, subtitle: null, authors: null }}
              className={styles.shortLine}
            />
          </div>
          {page.aboutExpert && (
            <AboutExpert {...page.aboutExpert} className={styles.headerAboutExpert} />
          )}
        </div>
        <SplitterLine className={styles.splitter} />

        {page.slots.beforeTable.length > 0 && (
          <div className={styles.slotBeforeTable}>
            {page.slots.beforeTable.map((item, index) => (
              <NC key={index} index={index} data={item} tpl="event" />
            ))}
          </div>
        )}

        <BestListComparisonTable
          defaultSortValue={page.filter.filterSettings.defaultSort}
          content={
            pageState.table.rows.length <= 0 && (
              <div className={styles.noResults}>
                <p className={styles.noResultsText}>{page.noResults}</p>
              </div>
            )
          }
          filterBlock={
            <div ref={ref}>
              <RWButton
                className={styles.filterButton}
                variant="tertiary"
                size="medium"
                onClick={() => {
                  setFilterOpen(o => !o)
                }}
              >
                <Filter className={styles.filterIcon} />
                {page.filter.filterButton}
              </RWButton>
              <BestListComparisonFilter
                className={clsx(styles.filter, {
                  [styles.filterOpen]: filterOpen,
                })}
                onSubmit={payload => {
                  setFilterOpen(false)
                  setCurrentFilterState(payload)
                }}
                settings={page.filter.filterSettings}
                collapsible={!(ready && md)}
                currentValues={page.filter.filterSettings.form.defaultState}
              />
            </div>
          }
          onSeeMore={handleSeeMore}
          onVote={handleVote}
          table={page.comparisonTable}
          tableContent={pageState.table}
        />
        <ContentLayout>
          {pageState.chart.products.length > 0 && (
            <div className={styles.comparisonChartWrapper}>
              <h2 className={clsx(styles.blockTitleResponsive, styles.comparisonChartTitle)}>
                {page.productChart.title}
              </h2>
              <ProductComparisonChart
                popupTitle={page.productChart.title}
                onSeeMore={handleSeeMore}
                data={pageState.chart}
              />
            </div>
          )}
          {page.slots.underChart.length > 0 && (
            <div className={styles.slotUnderChart}>
              {page.slots.underChart.map((item, index) => (
                <NC key={index} index={index} data={item} tpl="event" />
              ))}
            </div>
          )}
          {page.verdict && (
            <div className={styles.titledBlock}>
              <div className={clsx(styles.verdictText)}>
                {page.verdict.content.map((item, index) => (
                  <NC key={index} index={index} data={item} tpl="event" />
                ))}
              </div>
            </div>
          )}
          {page.slots.underVerdict.length > 0 && (
            <div className={styles.slotUnderVerdict}>
              {page.slots.underVerdict.map((item, index) => (
                <NC key={index} index={index} data={item} tpl="event" />
              ))}
            </div>
          )}
          {page.productsGallery && (
            <div className={styles.titledBlock}>
              <h2 className={clsx(styles.blockTitleResponsive, styles.blockTitleCompact)}>
                {page.productsGallery.title}
              </h2>
              <CompareDealGallery
                className={styles.compareGallery}
                {...page.productsGallery.gallery}
              />
            </div>
          )}
          {page.slots.underLatestProducts.length > 0 && (
            <div className={styles.slotUnderLatestProducts}>
              {page.slots.underLatestProducts.map((item, index) => (
                <NC key={index} index={index} data={item} tpl="event" />
              ))}
            </div>
          )}

          <PostTags meta={page.meta} className={styles.postTags} />

          {page.pagePath && page.meta.engagementContainerId ? (
            <ArticleConversation className={styles.comments} />
          ) : null}
        </ContentLayout>
      </div>

      <LocalPopup
        className={clsx(styles.seeMorePopup, {
          [styles.seeMorePopupScrolledDown]: !scrolledUp,
        })}
        onBackgroundClick={() => setSeeMorePopupOpened(false)}
        open={seeMorePopupOpened}
      >
        {seeMorePopupContent && (
          <SeeMoreProductPopup
            onVote={handleVote}
            onClose={() => setSeeMorePopupOpened(false)}
            {...seeMorePopupContent}
            className={styles.seeMorePopupContent}
          />
        )}
      </LocalPopup>
    </>
  )
}

export default BestListRatingsTemplate
